import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Coins, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const money = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);
const MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const fmtMonth = (m) => (m && m.length === 7 ? `${MONTHS[parseInt(m.slice(5), 10) - 1]} ${m.slice(0, 4)}` : "Senza data");
const thisMonth = () => new Date().toISOString().slice(0, 7);

// Elenco mesi selezionabili: 12 indietro + 3 avanti
const monthOptions = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + 3);
  return Array.from({ length: 16 }, () => {
    const m = d.toISOString().slice(0, 7);
    d.setMonth(d.getMonth() - 1);
    return m;
  });
};

function Tile({ label, value, sub, cls, testid }) {
  return (
    <div className={`border-2 p-3 ${cls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{label}</div>
      <div className="font-display text-2xl mt-1">{money(value)}</div>
      {sub && <div className="text-[10px] opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
}

function MonthsTable({ months }) {
  if (!months.length) return null;
  const cur = thisMonth();
  return (
    <table className="w-full text-xs border-2 border-[#0A0A0A]" data-testid="commissions-months-table">
      <thead className="bg-[#0A0A0A] text-white uppercase tracking-widest text-[10px]">
        <tr>
          <th className="text-left p-2">Mese</th>
          <th className="text-right p-2">Prevista (se il cliente paga)</th>
          <th className="text-right p-2">Maturata (cliente ha pagato)</th>
          <th className="text-right p-2">Ricevuta</th>
        </tr>
      </thead>
      <tbody>
        {months.map((m) => (
          <tr key={m.month} className={`border-t border-[#0A0A0A]/15 ${m.month === cur ? "bg-orange-50 font-bold" : ""}`} data-testid={`commissions-month-${m.month}`}>
            <td className="p-2">{fmtMonth(m.month)}{m.month === cur ? " · questo mese" : ""}</td>
            <td className="p-2 text-right">{m.prevista ? money(m.prevista) : "—"}</td>
            <td className="p-2 text-right text-emerald-700">{m.maturata ? money(m.maturata) : "—"}</td>
            <td className="p-2 text-right text-[#FF5A1F]">{m.ricevuta ? money(m.ricevuta) : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PayoutRow({ row, scope, onSaved }) {
  const [saving, setSaving] = useState(false);
  const save = async (mese) => {
    setSaving(true);
    try {
      await api.post("/commissioni/payout", { contract_id: row.contract_id, key: row.key, scope, mese: mese || null });
      toast.success(mese ? `Provvigione segnata come ricevuta · ${fmtMonth(mese)}` : "Segnata come da ricevere");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };
  const id = `${row.contract_id}-${row.key}`;
  return (
    <div className="flex flex-wrap items-center gap-3 border-t border-[#0A0A0A]/15 py-2 text-xs" data-testid={`payout-row-${id}`}>
      <div className="flex-1 min-w-[200px]">
        <b className="uppercase">{row.artist_name}</b> · {row.label} {money(row.importo)}
        <span className="text-[#0A0A0A]/50"> · incassata {fmtMonth(row.paid_month)}</span>
        {scope === "setter" && row.vendor_name && <span className="text-[#0A0A0A]/50"> · venditore {row.vendor_name}</span>}
        {scope === "setter" && row.setter_name && <span className="text-[#0A0A0A]/50"> · setter {row.setter_name}</span>}
      </div>
      <div className="font-bold">{money(row.commission)} <span className="text-[#0A0A0A]/40 font-normal">({row.pct}%)</span></div>
      <label className="flex items-center gap-1">
        <span className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/60">Ricevuta con:</span>
        <select
          value={row.payout_month}
          disabled={saving}
          onChange={(e) => save(e.target.value)}
          className={`border-2 px-2 py-1 text-[11px] font-bold ${row.payout_month ? "border-[#FF5A1F] text-[#FF5A1F]" : "border-[#0A0A0A]"}`}
          data-testid={`payout-month-${id}`}
        >
          <option value="">— Da ricevere —</option>
          {monthOptions().map((m) => <option key={m} value={m}>{fmtMonth(m)}</option>)}
        </select>
      </label>
    </div>
  );
}

export const CommissionsPanel = ({ scope = "vendor", title }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/commissioni", { params: { scope } });
      setData(r.data);
    } catch {
      toast.error("Errore caricamento provvigioni");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (open && !data) load(); /* eslint-disable-next-line */ }, [open]);

  const paidRows = (data?.rows || []).filter((r) => r.paid && (showAll || !r.payout_month));
  return (
    <section className="mb-8 border-2 border-[#0A0A0A] bg-white" data-testid={`commissions-panel-${scope}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-[#F2F2EC] transition-colors"
        data-testid={`commissions-toggle-${scope}`}
      >
        <span className="flex items-center gap-2 font-display text-xl uppercase">
          <Coins size={18} className="text-[#FF5A1F]" /> {title}
        </span>
        <span className="flex items-center gap-3 text-xs">
          {data && <span className="font-bold text-emerald-700">Da ricevere: {money(data.totals.maturata_da_ricevere)}</span>}
          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      </button>
      {open && (
        <div className="p-4 pt-0 space-y-5">
          {loading && !data && <Loader2 className="animate-spin" size={18} />}
          {data && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Tile label="Prevista futura" value={data.totals.prevista} sub="rate non ancora pagate dal cliente" cls="border-[#0A0A0A]/30" testid={`commissions-tot-prevista-${scope}`} />
                <Tile label="Maturata da ricevere" value={data.totals.maturata_da_ricevere} sub="il cliente ha pagato" cls="border-emerald-600 bg-emerald-50 text-emerald-800" testid={`commissions-tot-maturata-${scope}`} />
                <Tile label="Già ricevuta" value={data.totals.ricevuta} cls="border-[#FF5A1F] bg-orange-50 text-[#FF5A1F]" testid={`commissions-tot-ricevuta-${scope}`} />
                <Tile label="Totale contratti firmati" value={data.totals.totale_spettante} cls="border-[#0A0A0A] bg-[#0A0A0A] text-white" testid={`commissions-tot-totale-${scope}`} />
              </div>
              {data.rows.length === 0 ? (
                <div className="text-sm text-[#0A0A0A]/60" data-testid={`commissions-empty-${scope}`}>
                  Nessun contratto firmato con piano di pagamento{scope === "setter" ? " collegato a lead assegnati dal Setting" : ""}.
                </div>
              ) : (
                <>
                  <MonthsTable months={data.months} />
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#FF5A1F]">Rate incassate · segna il mese in cui hai ricevuto la provvigione</div>
                      <label className="flex items-center gap-1 text-[11px]">
                        <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} data-testid={`commissions-show-all-${scope}`} />
                        Mostra anche già ricevute
                      </label>
                    </div>
                    {paidRows.length === 0 ? (
                      <div className="text-xs text-[#0A0A0A]/50 py-2">Nessuna provvigione maturata da ricevere.</div>
                    ) : paidRows.map((r) => (
                      <PayoutRow key={`${r.contract_id}-${r.key}`} row={r} scope={scope} onSaved={load} />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
};
